import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Mail,
  MessageSquare,
  Package,
  Send,
  Sparkles,
  Truck,
  User,
} from 'lucide-react';
import CustomerLookup, { emptyAddress } from '../../components/quote/CustomerLookup';
import ProductSearch from '../../components/quote/ProductSearch';
import QuoteGrid from '../../components/quote/QuoteGrid';
import TotalsPanel, { formatMoney } from '../../components/quote/TotalsPanel';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { showFormError } from '../../utils/formFeedback';
import { calcQuoteTotals, normalizeItems } from './quoteUtils';
import './create-quote.css';

const SALES_REPS = [
  { id: 'admin', labelKey: 'quoteForm.salesReps.admin' },
  { id: 'rep1', labelKey: 'quoteForm.salesReps.rep1' },
];

const NOTE_PANES = [
  {
    id: 'customer',
    labelKey: 'quoteForm.noteCustomer',
    field: 'customer',
    icon: FileText,
    helpKey: 'quoteForm.noteCustomerHelp',
  },
  {
    id: 'message',
    labelKey: 'quoteForm.noteMessage',
    field: 'messageToCustomer',
    icon: MessageSquare,
    helpKey: 'quoteForm.noteMessageHelp',
  },
  {
    id: 'staff',
    labelKey: 'quoteForm.noteStaff',
    field: 'staff',
    icon: User,
    helpKey: 'quoteForm.noteStaffHelp',
  },
];

const quillModules = {
  toolbar: [
    ['bold', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
};

const EditQuotePage = ({ quoteId, onNavigate, storeCurrency = 'USD' }) => {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    customerGroupId: '',
    address: emptyAddress(),
  });
  const [quoteNumber, setQuoteNumber] = useState('');
  const [quoteStatus, setQuoteStatus] = useState('');
  const [salesRepId, setSalesRepId] = useState('admin');
  const [expiryDate, setExpiryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [notes, setNotes] = useState({ customer: '', messageToCustomer: '', staff: '' });
  const [notePane, setNotePane] = useState('customer');
  const [items, setItems] = useState([]);
  const [currency, setCurrency] = useState(storeCurrency || 'USD');
  const [bulkDiscountPct, setBulkDiscountPct] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [shippingAdditional, setShippingAdditional] = useState(0);
  const [shippingMethodName, setShippingMethodName] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getQuote(quoteId);
      const q = data?.quote;
      if (!q) throw new Error(t('quoteForm.toast.quoteNotFound'));
      setQuoteNumber(q.number || '');
      setQuoteStatus(q.status || '');
      setCustomer({
        name: q.customer?.name || '',
        email: q.customer?.email || '',
        company: q.customer?.company || '',
        phone: q.customer?.phone || '',
        customerGroupId: q.customer?.customerGroupId || '',
        address: { ...emptyAddress(), ...(q.customer?.address || {}) },
      });
      setSalesRepId(q.salesRepId || 'admin');
      setExpiryDate(q.expiryDate ? String(q.expiryDate).slice(0, 10) : '');
      setDeliveryTime(q.deliveryTime || '');
      setNotes({
        customer: q.notes?.customer || '',
        messageToCustomer: q.notes?.messageToCustomer || '',
        staff: q.notes?.staff || '',
      });
      setItems(normalizeItems(q.items || []));
      setBulkDiscountPct(q.bulkDiscountPct ?? 0);
      setShipping(q.shipping ?? 0);
      setShippingAdditional(q.shippingAdditional ?? 0);
      setShippingMethodName(q.shippingMethodName || '');
      setCurrency(q.currency || storeCurrency || 'USD');
      setCcEmails(Array.isArray(q.ccEmails) ? q.ccEmails.join(', ') : '');
    } catch (err) {
      showToast(err.message || t('quoteForm.toast.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [quoteId, storeCurrency, t]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(
    () => calcQuoteTotals(items, bulkDiscountPct, shipping, shippingAdditional),
    [items, bulkDiscountPct, shipping, shippingAdditional],
  );

  const lineQty = useMemo(
    () => items.reduce((s, i) => s + (Number(i.qty) || 0), 0),
    [items],
  );

  const readiness = useMemo(() => {
    const hasCustomer = Boolean(customer.name?.trim());
    const hasItems = items.length > 0;
    const hasEmail = Boolean(customer.email?.trim());
    return {
      hasCustomer,
      hasItems,
      hasEmail,
      draftReady: hasCustomer && hasItems,
      sendReady: hasCustomer && hasItems && hasEmail,
    };
  }, [customer.name, customer.email, items.length]);

  const activeNote = NOTE_PANES.find((p) => p.id === notePane) || NOTE_PANES[0];
  const ActiveNoteIcon = activeNote.icon;
  const salesRepKey = SALES_REPS.find((r) => r.id === salesRepId)?.labelKey;
  const salesRepLabel = salesRepKey ? t(salesRepKey) : salesRepId;

  const handleItemsChange = (next) => {
    setItems(normalizeItems(next));
  };

  const handleTotalsChange = (patch) => {
    if (patch.bulkDiscountPct != null) setBulkDiscountPct(patch.bulkDiscountPct);
    if (patch.shipping != null) setShipping(patch.shipping);
    if (patch.shippingAdditional != null) setShippingAdditional(patch.shippingAdditional);
    if (patch.shippingMethodName != null) setShippingMethodName(patch.shippingMethodName);
    if (patch.ccEmails != null) setCcEmails(patch.ccEmails);
  };

  const buildPayload = () => {
    const cc = ccEmails
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      customer: {
        name: customer.name,
        email: customer.email || '',
        company: customer.company,
        phone: customer.phone,
        customerGroupId: customer.customerGroupId || undefined,
        address: customer.address,
      },
      currency,
      salesRepId: salesRepId || undefined,
      expiryDate: expiryDate || undefined,
      deliveryTime: deliveryTime || undefined,
      notes: {
        customer: notes.customer || undefined,
        messageToCustomer: notes.messageToCustomer || undefined,
        staff: notes.staff || undefined,
      },
      items: normalizeItems(items).map(({ id, ...rest }) => ({ ...rest, id })),
      bulkDiscountPct,
      shipping,
      shippingAdditional,
      shippingMethodName: shippingMethodName || undefined,
      taxPct: 0,
      ccEmails: cc.length ? cc : undefined,
    };
  };

  const validate = () => {
    if (!customer.name?.trim()) {
      showFormError(t('quoteForm.toast.customerNameRequired'), {
        target: 'q2c-cq-customer',
      });
      return false;
    }
    if (!items.length) {
      showFormError(t('quoteForm.toast.addOneProduct'), {
        target: 'q2c-cq-products',
      });
      return false;
    }
    if (!String(shippingMethodName || '').trim()) {
      showFormError(t('quoteForm.toast.shippingRateRequired'), {
        target: 'q2c-cq-shipping-rate',
      });
      return false;
    }
    return true;
  };

  const persistQuote = async () => {
    const data = await api.updateQuote(quoteId, buildPayload());
    return data.quote;
  };

  const onSaveDraft = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await persistQuote();
      await api.saveDraft(quoteId);
      showToast(t('quoteForm.toast.draftSavedSimple'), 'success');
      onNavigate(`quotes/${quoteId}`);
    } catch (err) {
      showToast(err.message || t('quoteForm.toast.saveDraftFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const onEmailPreview = async () => {
    if (!validate()) return;
    setPreviewEmail(customer.email || '');
    setPreviewOpen(true);
  };

  const sendPreview = async () => {
    if (!previewEmail.trim()) {
      showToast(t('quoteForm.toast.enterPreviewEmail'), 'warning');
      return;
    }
    setBusy(true);
    try {
      await persistQuote();
      await api.previewEmail(quoteId, previewEmail.trim());
      showToast(t('quoteForm.toast.previewPrepared', { email: previewEmail }), 'success');
      setPreviewOpen(false);
    } catch (err) {
      showToast(err.message || t('quoteForm.toast.previewFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const onSendQuote = async () => {
    if (!validate()) return;
    if (!customer.email?.trim()) {
      showFormError(t('quoteForm.toast.customerEmailRequired'), {
        target: 'q2c-cq-customer',
      });
      return;
    }
    setBusy(true);
    try {
      await persistQuote();
      await api.sendQuote(quoteId);
      showToast(t('quoteForm.toast.quoteSentSimple'), 'success');
      onNavigate(`quotes/${quoteId}`);
    } catch (err) {
      showToast(err.message || t('quoteForm.toast.sendFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="q2c-cq">
      <section className="q2c-cq-hero">
        <div className="q2c-cq-hero-copy">
          <div className="q2c-cq-kicker">
            <Sparkles size={14} />
            {t('quoteForm.kicker')}
          </div>
          <h2 className="q2c-cq-title">
            {quoteNumber ? t('quoteForm.editTitle', { number: quoteNumber }) : t('quoteForm.editTitleNoNumber')}
          </h2>
          <p className="q2c-cq-sub">
            {quoteStatus
              ? t('quoteForm.editSubStatus', { status: t(`status.${quoteStatus}`, { defaultValue: quoteStatus }) })
              : t('quoteForm.editSubDefault')}
          </p>
          <ol className="q2c-cq-steps" aria-label={t('quoteForm.editTitleNoNumber')}>
            <li className={readiness.hasCustomer ? 'is-done' : ''}>
              <span>1</span> {t('quoteForm.stepCustomer')}
            </li>
            <li className={readiness.hasItems ? 'is-done' : ''}>
              <span>2</span> {t('quoteForm.stepProducts')}
            </li>
            <li className={readiness.sendReady ? 'is-done' : ''}>
              <span>3</span> {t('quoteForm.stepSend')}
            </li>
          </ol>
        </div>

        <div className="q2c-cq-hero-side">
          <div className="q2c-cq-ready">
            <div className="q2c-cq-ready-label">{t('quoteForm.readiness')}</div>
            <ul className="q2c-cq-checklist">
              <li className={readiness.hasCustomer ? 'is-on' : ''}>
                <User size={14} />
                {t('quoteForm.customerName')}
                {readiness.hasCustomer ? <CheckCircle2 size={14} /> : null}
              </li>
              <li className={readiness.hasItems ? 'is-on' : ''}>
                <Package size={14} />
                {t('quoteForm.lineItems')}
                {readiness.hasItems ? <CheckCircle2 size={14} /> : null}
              </li>
              <li className={readiness.hasEmail ? 'is-on' : ''}>
                <Mail size={14} />
                {t('quoteForm.emailToSend')}
                {readiness.hasEmail ? <CheckCircle2 size={14} /> : null}
              </li>
            </ul>
          </div>
          <div className="q2c-cq-hero-total">
            <div className="q2c-cq-hero-total-label">{t('quoteForm.runningTotal')}</div>
            <div className="q2c-cq-hero-total-value">{formatMoney(totals.total, currency)}</div>
            <div className="q2c-cq-hero-total-meta">
              {t('quoteForm.heroMeta', {
                skus: t('quoteForm.skuCount', { count: items.length }),
                units: lineQty,
                currency,
              })}
            </div>
          </div>
        </div>
      </section>

      <div className="q2c-cq-layout">
        <section className="q2c-cq-panel" id="q2c-cq-customer">
          <header className="q2c-cq-panel-head">
            <span className="q2c-cq-step-badge">1</span>
            <div>
              <h3>{t('quoteForm.stepCustomer')}</h3>
              <p>{t('quoteForm.customerPanelSubEdit')}</p>
            </div>
          </header>
          <div className="q2c-cq-customer">
            <CustomerLookup value={customer} onChange={setCustomer} />
          </div>
        </section>

        <aside className="q2c-cq-side">
          <section className="q2c-cq-panel q2c-cq-panel--tight">
            <header className="q2c-cq-panel-head">
              <span className="q2c-cq-step-badge">·</span>
              <div>
                <h3>{t('quoteForm.quoteOptions')}</h3>
                <p>{t('quoteForm.quoteOptionsSub')}</p>
              </div>
            </header>

            <Form.Group className="mb-3">
              <Form.Label className="q2c-cq-label">
                <User size={14} /> {t('quoteForm.salesRep')}
              </Form.Label>
              <Form.Select value={salesRepId} onChange={(e) => setSalesRepId(e.target.value)}>
                {SALES_REPS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {t(r.labelKey)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="q2c-cq-label">
                <Calendar size={14} /> {t('quoteForm.expiryDate')}
              </Form.Label>
              <Form.Control
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="q2c-cq-label">
                <Truck size={14} /> {t('quoteForm.deliveryTime')}
              </Form.Label>
              <Form.Control
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
                placeholder={t('quoteForm.deliveryTimePlaceholder')}
              />
            </Form.Group>

            <div className="q2c-cq-option-chips">
              <span>
                <User size={12} /> {salesRepLabel}
              </span>
              {expiryDate ? (
                <span>
                  <Calendar size={12} /> {expiryDate}
                </span>
              ) : null}
              {deliveryTime ? (
                <span>
                  <Clock size={12} /> {deliveryTime}
                </span>
              ) : null}
            </div>
          </section>

          <section className="q2c-cq-panel q2c-cq-panel--tight">
            <header className="q2c-cq-panel-head">
              <span className="q2c-cq-step-badge">·</span>
              <div>
                <h3>{t('quoteForm.notes')}</h3>
                <p>{t('quoteForm.notesSub')}</p>
              </div>
            </header>

            <div className="q2c-cq-note-nav" role="tablist">
              {NOTE_PANES.map((pane) => {
                const Icon = pane.icon;
                return (
                  <button
                    key={pane.id}
                    type="button"
                    role="tab"
                    aria-selected={notePane === pane.id}
                    className={notePane === pane.id ? 'is-active' : ''}
                    onClick={() => setNotePane(pane.id)}
                  >
                    <Icon size={13} />
                    {t(pane.labelKey)}
                  </button>
                );
              })}
            </div>

            <div className="q2c-cq-note-pane">
              <div className="q2c-cq-note-title">
                <ActiveNoteIcon size={15} />
                {t(activeNote.labelKey)}
              </div>
              <p className="q2c-cq-hint">{t(activeNote.helpKey)}</p>
              {NOTE_PANES.map((pane) => (
                <div
                  key={pane.id}
                  className="q2c-cq-quill"
                  hidden={notePane !== pane.id}
                >
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    value={notes[pane.field] || ''}
                    onChange={(v) => {
                      const field = pane.field;
                      setNotes((n) =>
                        n[field] === v ? n : { ...n, [field]: v },
                      );
                    }}
                  />
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <section className="q2c-cq-panel" id="q2c-cq-products">
        <header className="q2c-cq-panel-head">
          <span className="q2c-cq-step-badge">2</span>
          <div>
            <h3>{t('quoteForm.stepProducts')}</h3>
            <p>{t('quoteForm.productsSub')}</p>
          </div>
          {items.length > 0 ? (
            <span className="q2c-cq-count-pill">
              {t('quoteForm.lineCount', { count: items.length })}
            </span>
          ) : null}
        </header>

        <div className="q2c-cq-products">
          <ProductSearch
            currency={currency}
            showCurrencySelector={false}
            onAdd={(item) => setItems((prev) => normalizeItems([...prev, item]))}
          />
          <div className="q2c-cq-grid-wrap">
            <div className="q2c-cq-grid-label">{t('quoteForm.linesInQuote')}</div>
            <QuoteGrid
              items={items}
              currency={currency}
              editable
              onChange={handleItemsChange}
              onRemove={(index) => setItems((prev) => prev.filter((_, i) => i !== index))}
            />
          </div>
        </div>
      </section>

      <section className="q2c-cq-panel q2c-cq-panel--totals">
        <header className="q2c-cq-panel-head">
          <span className="q2c-cq-step-badge">3</span>
          <div>
            <h3>{t('quoteForm.totalsSend')}</h3>
            <p>{t('quoteForm.totalsSendSub')}</p>
          </div>
        </header>

        <TotalsPanel
          currency={currency}
          itemCount={lineQty}
          listSubtotal={totals.listSubtotal}
          subtotal={totals.subtotal}
          lineDiscountAmount={totals.lineDiscountAmount}
          bulkDiscountPct={bulkDiscountPct}
          discountAmount={totals.discountAmount}
          shipping={shipping}
          shippingAdditional={shippingAdditional}
          shippingMethodName={shippingMethodName}
          total={totals.total}
          ccEmails={ccEmails}
          editable
          customer={customer}
          lines={items}
          onChange={handleTotalsChange}
        />
      </section>

      <div className="q2c-cq-sticky">
        <div className="q2c-cq-sticky-meta">
          <strong>{formatMoney(totals.total, currency)}</strong>
          <span>
            {readiness.sendReady
              ? t('quoteForm.readyToSend')
              : readiness.draftReady
                ? t('quoteForm.readyToDraft')
                : t('quoteForm.addToContinue')}
          </span>
        </div>
        <div className="q2c-cq-sticky-actions">
          <Button variant="secondary" disabled={busy} onClick={onSaveDraft}>
            {t('quoteForm.saveDraft')}
          </Button>
          <Button variant="outline-light" disabled={busy} onClick={onEmailPreview}>
            {t('quoteForm.preview')}
          </Button>
          <Button
            variant="warning"
            className="text-dark fw-bold"
            disabled={busy}
            onClick={onSendQuote}
          >
            <Send size={15} className="me-1" />
            {t('quoteForm.sendQuote')}
          </Button>
        </div>
      </div>

      {busy ? (
        <div className="q2c-cq-busy">
          <Spinner animation="border" size="sm" /> {t('quoteForm.working')}
        </div>
      ) : null}

      <Modal show={previewOpen} onHide={() => setPreviewOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('quoteForm.emailPreview')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t('quoteForm.sendPreviewTo')}</Form.Label>
            <Form.Control
              type="email"
              value={previewEmail}
              onChange={(e) => setPreviewEmail(e.target.value)}
              placeholder={t('quoteForm.previewPlaceholder')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={sendPreview} disabled={busy}>
            {t('quoteForm.sendPreview')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditQuotePage;
